import { useState } from "react";
import { CaretDown, Check } from "@phosphor-icons/react";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Searchable dropdown built on cmdk + radix popover.
 *
 * Type-to-search filters options as the user types. Options can carry
 * `sub` (small caption below the label — e.g. worker email) and
 * `keywords` (extra strings matched during filtering — e.g. worker email
 * or last name so typing "smith" or "jsmith@x.com" both find the same row).
 *
 * The popover width tracks the trigger via the `--radix-popover-trigger-width`
 * CSS var Radix exposes, so form-embedded pickers don't get cropped.
 */
export function Combobox({
  value,
  onChange,
  options,
  placeholder = "Select…",
  emptyText = "No matches.",
  searchPlaceholder = "Type to search…",
  disabled = false,
  required = false,
  className = "",
  testId,
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-required={required}
          data-testid={testId}
          disabled={disabled}
          className={cn(
            "w-full h-11 px-3 border border-slate-300 rounded-md text-left bg-white",
            "flex items-center justify-between",
            "disabled:bg-slate-100 disabled:cursor-not-allowed",
            !selected && "text-slate-400",
            className,
          )}
        >
          <span className="truncate">
            {selected ? (selected.selectedLabel || selected.label) : placeholder}
          </span>
          <CaretDown size={14} className="text-slate-500 shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-[--radix-popover-trigger-width] max-w-[95vw]"
      >
        <Command
          filter={(itemVal, search) => {
            if (!search) return 1;
            return itemVal.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  // Compose the searchable string so keywords + sub-text are matchable
                  value={`${o.label} ${o.sub || ""} ${(o.keywords || []).join(" ")}`}
                  onSelect={() => { onChange(o.value); setOpen(false); }}
                  data-testid={o.testId}
                  className={cn(o.highlight && "text-emerald-800 font-semibold")}
                >
                  <Check
                    size={14}
                    className={cn(
                      "mr-2 shrink-0",
                      value === o.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="truncate">{o.label}</span>
                    {o.sub && <span className="truncate text-xs text-slate-500">{o.sub}</span>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
